import React, { MutableRefObject, useEffect, useRef, useState } from 'react'
import styles from '~/styles/molecules/navigation/navigation.module.scss'
import { useTranslation } from 'react-i18next'

import { ColorWeight, lightenColor, SpaceColor } from '~/common/define/color'
import { GeneralIconTag } from '~/common/define/icon'
import { SingleMenuMode } from '~/common/define/menu'
import { addClass } from '~/common/utils/html'
import { getRect, isEllipsisActive } from '~/common/utils/rect'
import { onceTimer, SHORT_INTERVAL_MILI, SPREAD_INTERVAL_MILI } from '~/common/utils/timer'
import NavigationMenuList from '~/components/molecules/navigation/menu/NavigationMenuList'
import { NavigationMenuItemVar } from '~/components/molecules/navigation/menu/NavigationMenuItem'
import Icon, { IconColor, IconFrame, IconSize } from '~/components/atoms/icon/Icon'
import ModalActionButton from '~/components/atoms/button/ModalActionButton'
import { ButtonSize } from '~/components/atoms/button/ButtonBase'
import { useSortableItem } from '~/components/utils/sort/sotableItem'
import IconText from '~/components/atoms/icon/IconText'
import IconButton from '~/components/atoms/icon/IconButton'
import NavigationMenuNew from '~/components/molecules/navigation/menu/NavigationMenuNew'
import TooltipWrapper from '~/components/wrapper/tooltip/TooltipWrapper'

interface Props {
  id: number
  title: string
  color: SpaceColor
  items: NavigationMenuItemVar[]
  supportText: string
  onAdd(top: number, left: number): void
  onSort(): void
  onEdit(): void
  onSortMenus(): void
  onCompleteSort(): void
  addTooltipText: string
  mode: SingleMenuMode
  onListUpdated(items: NavigationMenuItemVar[]): void
  onClickSupport?(): void
  onlyItems?: boolean
  smallAdd?: boolean
  menuRef?: MutableRefObject<any>
  toolNewRef?: MutableRefObject<any>
}

const NavigationMenu = React.memo(({id, title, color, items, supportText, onAdd, onSort, onEdit, onSortMenus, onCompleteSort,
                                     addTooltipText, mode, onListUpdated, onClickSupport = null, onlyItems = false, smallAdd = false, menuRef = null, toolNewRef = null}: Props) => {
  const {t} = useTranslation()
  const ref = useRef()
  const titleRef = useRef()
  const [itemsShown, setItemsShown] = useState<boolean>(true)
  const active = mode == SingleMenuMode.VIEW || mode == SingleMenuMode.MENU_SORT
  const sortableItemProps = useSortableItem(id, mode != SingleMenuMode.MENU_SORT)

  useEffect(() => {
    const show = mode != SingleMenuMode.MENU_SORT
    onceTimer(() => {
      setItemsShown(show)
    }, show ? SHORT_INTERVAL_MILI : SPREAD_INTERVAL_MILI)
  }, [mode == SingleMenuMode.MENU_SORT])

  const onClickAdd = () => {
    if (active) {
      const rect = getRect(ref)
      onAdd(rect.right, rect.top)
    }
  }

  return (
    <div className={styles.menu} ref={ref} {...sortableItemProps}>
      {!onlyItems &&
        <div className={styles.menu__title} ref={menuRef}>
          <IconButton onClick={() => active && onSortMenus()} toolTipText={t('navigation:tooltip.sort')} toolTipOff={mode != SingleMenuMode.VIEW}
                      tag={GeneralIconTag.DRAGGABLE} size={IconSize.X_SMALL}
                      frame={IconFrame.CIRCLE} fillColor={active ? color : IconColor.DARK_GRAY}
                      bgColor={active? lightenColor(color, ColorWeight.WELTER) : IconColor.GRAY}
          />
          <TooltipWrapper text={title} off={!isEllipsisActive(titleRef)} className={styles.menu__title__text} wrapRef={titleRef}>
            {title}
          </TooltipWrapper>
          {mode == SingleMenuMode.VIEW &&
            <span className={styles.menu__title__edit}>
              <IconButton onClick={() => active && onEdit()} toolTipText={t('navigation:tooltip.edit')}
                          tag={GeneralIconTag.EDIT} size={IconSize.X_SMALL}
                          fillColor={IconColor.DARK_GRAY} frame={IconFrame.CIRCLE} frameColor={IconColor.DARK_GRAY}
                          hoveredShowFrame
              />
            </span>
          }
          <span className={styles.menu__title__support + addClass(styles.keep, mode == SingleMenuMode.SORT)}>
          {mode == SingleMenuMode.VIEW &&
            <>
              <IconButton onClick={onSort} toolTipText={t('navigation:tooltip.sort')}
                          tag={GeneralIconTag.SORT} size={IconSize.X_SMALL}
                          frame={IconFrame.CIRCLE} fillColor={IconColor.WHITE} bgColor={color} clickableColor={color}
              />
              <IconButton toolTipText={addTooltipText}
                          tag={GeneralIconTag.ADD_CROSS} size={IconSize.X_SMALL}
                          frame={IconFrame.CIRCLE} fillColor={IconColor.WHITE} bgColor={color} clickableColor={color}
                          onClick={onClickAdd}
              />
            </>
          }
            {mode == SingleMenuMode.SORT &&
              <ModalActionButton onClick={onCompleteSort} size={ButtonSize.SMALL}>
                {t('navigation:complete')}
              </ModalActionButton>
            }
        </span>
        </div>
      }
      <div className={styles.menu__sortOpen + addClass(styles.menu__sortClose, mode == SingleMenuMode.MENU_SORT || !itemsShown)
        + addClass(styles.menu__sortClose__hidden, mode == SingleMenuMode.MENU_SORT && !itemsShown)}>
        <NavigationMenuList items={items} mode={mode} color={color} onListUpdated={onListUpdated}/>
        {supportText && onClickSupport ?
          <button className={styles.menu__support + addClass(styles.disable, mode != SingleMenuMode.VIEW)}
                  onClick={onClickSupport} ref={toolNewRef}
          >
            {supportText}
          </button> :
          smallAdd ?
            <button
              className={styles.menu__support + addClass(styles.disable, mode != SingleMenuMode.VIEW)}
              onClick={onClickAdd} ref={toolNewRef}
            >
              <IconText
                color={color}
                tag={GeneralIconTag.ADD_CROSS} size={IconSize.XXXX_SMALL}
                fillColor={IconColor.WHITE} frame={IconFrame.CIRCLE}
                bgColor={mode == SingleMenuMode.VIEW ? color : IconColor.GRAY}
                clickableColor={mode == SingleMenuMode.VIEW ? color : null}
              >
                {supportText}
              </IconText>
            </button> :
            <NavigationMenuNew color={color} onClick={onClickAdd} text={supportText}/>
        }
      </div>
      <style jsx>{`
        div, button {
          color: ${active ? color : IconColor.GRAY};
        }
      `}</style>
    </div>
  )
})

export default NavigationMenu